import React, { useState, useCallback, useEffect } from 'react';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  Typography,
  Alert,
  AlertTitle,
} from '@mui/material';
import { useAuth } from '../../../contexts/AuthContext';
import { previewActivationCode, claimActivationCode } from '../../../api/backendApi';

const CODE_LENGTH = 6;

/**
 * Modal to look up and claim an Embedded Agent activation code for the current tenant.
 */
const AddEmbeddedAgentModal = ({ open, onClose, onSuccess }) => {
  const { userData } = useAuth();

  const tenantId = userData?.tenantId;
  const tenantName = userData?.tenantName || tenantId || 'this tenant';
  const role = userData?.role;
  const hasAdminAccess = role === 'superadmin';

  const [code, setCode] = useState('');
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [claiming, setClaiming] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);

  useEffect(() => {
    if (!open) return;
    setCode('');
    setPreview(null);
    setError(null);
    setSuccess(false);
    setConfirmOpen(false);
    setLoading(false);
    setClaiming(false);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    if (!tenantId || !hasAdminAccess) {
      setError('Only super administrators can add an Embedded Agent.');
    }
  }, [open, tenantId, hasAdminAccess]);

  const normalizeCode = raw =>
    raw
      .toUpperCase()
      .replace(/[^ABCDEFGHJKMNPQRSTVWXYZ23456789]/g, '')
      .slice(0, CODE_LENGTH);

  const handleCodeChange = e => {
    setCode(normalizeCode(e.target.value));
    setPreview(null);
    setSuccess(false);
    setError(null);
  };

  const handlePreview = useCallback(async () => {
    if (code.length !== CODE_LENGTH) {
      setError(`Please enter a ${CODE_LENGTH}-character activation code.`);
      return;
    }
    setLoading(true);
    setError(null);
    setPreview(null);
    setSuccess(false);
    try {
      const response = await previewActivationCode(code);
      if (response?.success && response.data) {
        setPreview(response.data);
      } else {
        setError(response?.error || 'Could not look up activation code.');
      }
    } catch (err) {
      setError(err.message || 'Failed to look up activation code.');
    } finally {
      setLoading(false);
    }
  }, [code]);

  const handleClaim = useCallback(async () => {
    setConfirmOpen(false);
    setClaiming(true);
    setError(null);
    setSuccess(false);
    try {
      const response = await claimActivationCode(code, tenantId);
      if (response?.success) {
        setSuccess(true);
        setPreview(null);
        setCode('');
        if (typeof onSuccess === 'function') {
          onSuccess(response);
        }
      } else {
        setError(response?.error || 'Failed to claim the Embedded Agent.');
      }
    } catch (err) {
      setError(err.message || 'Failed to claim the Embedded Agent.');
    } finally {
      setClaiming(false);
    }
  }, [code, tenantId, onSuccess]);

  return (
    <>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>Add Embedded Agent</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter the {CODE_LENGTH}-character activation code shown on the device.
          </Typography>

          {!hasAdminAccess ? (
            <Alert severity="error">
              <AlertTitle>Access denied</AlertTitle>
              You do not have permission to add an Embedded Agent.
            </Alert>
          ) : (
            <>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
                <TextField
                  label="Activation Code"
                  value={code}
                  onChange={handleCodeChange}
                  inputProps={{ maxLength: CODE_LENGTH }}
                  disabled={loading || claiming}
                  autoFocus
                  fullWidth
                />
                <Button
                  variant="contained"
                  onClick={handlePreview}
                  disabled={loading || claiming || code.length !== CODE_LENGTH}
                  sx={{ whiteSpace: 'nowrap', minWidth: 100 }}
                >
                  {loading ? <CircularProgress size={20} /> : 'Look Up'}
                </Button>
              </Box>

              {error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {error}
                </Alert>
              )}

              {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                  <AlertTitle>Success</AlertTitle>
                  The Embedded Agent has been added to {tenantName}.
                </Alert>
              )}

              {preview && !success && (
                <Box sx={{ mt: 3 }}>
                  <Typography variant="h6" gutterBottom>
                    Device Details
                  </Typography>
                  <Typography variant="body2">
                    <strong>Device ID:</strong> {preview.deviceId}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Platform:</strong> {preview.deviceInfo?.os_version || 'Unknown'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Device Type:</strong> {preview.formFactor || 'Embedded Agent'}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Firmware Version:</strong> {preview.appVersion || 'Unknown'}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                    Expires at: {new Date(preview.expiresAt).toLocaleString()}
                  </Typography>

                  <Button
                    variant="contained"
                    color="primary"
                    onClick={() => setConfirmOpen(true)}
                    disabled={claiming}
                    sx={{ mt: 2 }}
                  >
                    {claiming ? <CircularProgress size={20} /> : 'Add to Tenant'}
                  </Button>
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} color="inherit">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>Confirm Add Embedded Agent</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Add this Embedded Agent to <strong>{tenantName}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} color="inherit">
            Cancel
          </Button>
          <Button onClick={handleClaim} variant="contained" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default AddEmbeddedAgentModal;
